/**
 * "Today's insights" on the home page: the signals computed from the ledgers (each a link to the
 * screen that deals with it) and, when a model is configured, its short narrative in the user's
 * language. Refreshable; hidden when the user has no insights grant.
 */
import { api } from '../lib/api.ts';
import { useQuery } from '../lib/hooks.ts';
import { useI18n } from '../lib/i18n.tsx';
import { Link } from '../lib/router.tsx';
import { useAi } from '../lib/ai.tsx';
import { Button, Card, Spinner, useToast } from './ui.tsx';
import { Icon } from './Icon.tsx';

interface Signal { code: string; severity: 'info' | 'warning' | 'critical'; title: string; titleAr: string; value: string; detail?: string; path?: string }
interface Digest { signals: Signal[]; narrative: string | null; narrativeAr: string | null; model: string | null; generatedAt: string; asOf: string }
const ICON: Record<Signal['severity'], string> = { critical: 'TriangleAlert', warning: 'Info', info: 'Circle' };

export function InsightsCard() {
  const { t, locale } = useI18n();
  const ai = useAi();
  const toast = useToast();
  const on = ai.can('insights');
  const { data, loading, refresh } = useQuery(on ? 'ai:insights' : null, () => api.get<Digest>('ai/insights'), { ttlMs: 300_000 });
  if (!on) return null;
  const narrative = data ? (locale === 'ar' ? data.narrativeAr ?? data.narrative : data.narrative) : null;
  const refreshNow = async () => { try { await api.get<Digest>('ai/insights', { refresh: 1 }); await refresh(); } catch (e) { toast.error(e); } };
  return (
    <Card title={<><Icon name="Sparkles" size={16} /> {t('ai.insights')}</>} className="insights" actions={<Button size="sm" variant="ghost" icon="RefreshCw" onClick={() => void refreshNow()} title={t('ai.refresh')} />}>
      {loading && !data ? <Spinner /> : !data ? null : (
        <div className="insights-body" data-insights>
          {narrative && <p className="insights-narrative">{narrative}</p>}
          {data.signals.length === 0 ? <div className="muted">{t('ai.insightsEmpty')}</div> : (
            <ul className="signals">
              {data.signals.map((s) => (
                <li key={s.code} className={`signal ${s.severity}`}>
                  <Icon name={ICON[s.severity]} size={16} />
                  <span className="grow">{s.path ? <Link to={s.path} className="link">{locale === 'ar' ? s.titleAr : s.title}</Link> : (locale === 'ar' ? s.titleAr : s.title)}<span className="muted"> — {s.value}{s.detail ? ` · ${s.detail}` : ''}</span></span>
                </li>
              ))}
            </ul>
          )}
          {data.model && <div className="subtle">{t('ai.generatedBy', { model: data.model })} · {new Date(data.generatedAt).toLocaleTimeString()}</div>}
        </div>
      )}
    </Card>
  );
}
